/**
 * Беззвучная запись в кассу. Не форма: те же отметки ног, что уже есть.
 * keepalive — вкладка в этот миг уходит на контору.
 */

import { Decimal } from 'decimal.js';

import { leftoverPair } from './pnl';
import { apiUrl } from '../lib/build';
import { stakes } from '../lib/calc';
import type { Fork } from '../lib/protocol';
import { buildRow } from '../lib/row';
import { LAB_BUNDLE } from '../next/route';
import { usePaper } from '../next/paper';
import { parked } from '../next/parked';
import { useScreen } from '../next/store';
import { capsInput, tempoOf, useSettings } from '../store/settings';

export interface CaptureExtra {
  naked?: boolean;
  error?: boolean;
}

export function payloadFromFork(fork: Fork, extra: CaptureExtra = {}): Record<string, unknown> {
  const settings = useSettings.getState();
  const screen = useScreen.getState();
  const mine = <T extends { forkId: string }>(lock: T | null): T | undefined =>
    lock !== null && lock.forkId === fork.id ? lock : undefined;
  // В кассу — то, что человек видел и вбил: замки суммы, кэфа, валюты этой вилки.
  const row = buildRow(fork, {
    bank: settings.bank,
    fx: settings.fx,
    ...capsInput(settings),
    tempo: tempoOf(settings.attempts),
    roundStep: settings.roundStep,
    roundDir: settings.roundDir,
    roundWho: settings.roundWho,
    lock: mine(screen.stakeLock),
    odds: mine(screen.oddsLock),
    fees: mine(screen.feeLock),
    curs: mine(screen.curLock),
    free: mine(screen.freeLock),
    hand: mine(screen.handLock),
  });
  const odds = (fork.o ?? fork.legs?.map((leg) => leg.odds) ?? []).map(String);
  const plan = odds.length >= 2 ? stakes(String(settings.bank), odds) : [];
  const legs = (fork.legs ?? []).map((leg, i) => {
    const fromRow = row?.legs[i];
    const foreign = fromRow !== undefined && fromRow.cur !== 'RUB' && fromRow.usd !== null;
    return {
      venue: leg.s,
      side: leg.side,
      odds: fromRow?.odds ?? odds[i] ?? String(leg.odds),
      stake: fromRow === undefined
        ? (plan[i]?.toFixed(2) ?? '0')
        : foreign ? new Decimal(fromRow.usd!).toFixed(2) : new Decimal(fromRow.rub).toFixed(2),
      cur: fromRow?.cur ?? 'RUB',
      fx: foreign ? String(settings.fx) : '1',
      fee: '0',
      clvOpen: odds[i] ?? String(leg.odds),
    };
  });
  let promisedRoi: string | undefined;
  if (fork.roi !== undefined && fork.roi !== '') {
    const pct = Number(fork.roi);
    if (Number.isFinite(pct)) promisedRoi = String(pct / 100);
  }
  return {
    forkId: fork.id,
    sport: fork.sport ?? '',
    line: fork.line ?? '',
    market: fork.market ?? '',
    period: 'M',
    eventKey: fork.ev ?? '',
    promisedRoi,
    book: usePaper.getState().on ? 'paper' : 'live',
    legs,
    tzOffsetMin: -new Date().getTimezoneOffset(),
    bank: String(settings.bank),
    ...extra,
  };
}

export function tellBook(body: Record<string, unknown>): void {
  // Тестерская лента кассу не ведёт: иначе отметка ноги плодит журнал,
  // которого на /app нет. Пишет только бандл /lab.
  if (!LAB_BUNDLE && parked.book) return;
  void fetch(apiUrl('/v1/book/positions'), {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    keepalive: true,
  }).catch(() => {
    // Молча: касса не имеет права задержать ленту.
  });
}

export function tellBookFromFork(fork: Fork | undefined, extra: CaptureExtra = {}): void {
  if (fork === undefined) return;
  tellBook(payloadFromFork(fork, extra));
}

export function twoPluses(stakesFill: string[], oddsFill: string[]): [string, string] | null {
  if (stakesFill.length < 2 || oddsFill.length < 2) return null;
  const pair = leftoverPair(stakesFill, oddsFill);
  return [pair[0].toFixed(2), pair[1].toFixed(2)];
}
